import { Component, Input, Output, EventEmitter } from '@angular/core';

/**
 * FilterInputComponent
 *
 * Purpose
 * - PCC-styled search/filter input field with built-in search icon
 * - Designed specifically for filtering and searching functionality
 * - Supports labels, error states, disabled state, and automatic clear button
 * - Two-way data binding for real-time filtering
 *
 * Visual states
 * - default: standard input field with search icon on the left
 * - hover: subtle border highlight
 * - focus: blue border (#0671E0) with glow effect
 * - error: red border with error message display
 * - disabled: grayed out appearance, no interaction
 *
 * Features
 * - Built-in search icon (left side) - always visible
 * - Automatic clear button (X icon) appears when input has value
 * - Optional label above the input
 * - Optional error text with icon below the input
 * - Customizable width (default: 350px)
 *
 * Inputs
 * - placeholder: string (optional) - Placeholder text shown when input is empty
 * - model: string (default: '') - Two-way bindable filter/search value
 * - width: string (default: '350px') - Custom width for the input field (e.g., '100%', '200px')
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - label: string (optional) - Label text displayed above the input field
 * - hasError: boolean (default: false) - Applies error styling (red border)
 * - errorText: string (optional) - Error message displayed below the input when hasError is true
 * - size: 'large' | 'medium' | 'small' (default: 'medium') - Controls the size of the input field, affecting height, font sizes, border radius, and padding
 *
 * Outputs
 * - modelChange: string - Emits the new filter value on change (supports [(model)] two-way binding)
 * - enter: void - Emits when the user presses Enter in the input
 *
 * Usage examples
 *
 * <!-- Basic filter input -->
 * <app-filter-input placeholder="Search..." [(model)]="searchTerm"></app-filter-input>
 *
 * <!-- With label -->
 * <app-filter-input label="Search Users" placeholder="Enter search term" [(model)]="userFilter"></app-filter-input>
 *
 * <!-- Two-way binding for real-time filtering -->
 * <app-filter-input [(model)]="filterValue" placeholder="Filter items..." (modelChange)="onFilterChange($event)"></app-filter-input>
 *
 * <!-- With error state -->
 * <app-filter-input
 *   [(model)]="searchQuery"
 *   label="Search Query"
 *   [hasError]="hasValidationError"
 *   errorText="Search query is required">
 * </app-filter-input>
 *
 * <!-- Custom width -->
 * <app-filter-input [(model)]="globalSearch" placeholder="Search everything..." width="100%"></app-filter-input>
 *
 * <!-- Disabled state -->
 * <app-filter-input [model]="readonlyFilter" [disabled]="true" label="Filter (disabled)"></app-filter-input>
 *
 * <!-- With size variants -->
 * <app-filter-input [(model)]="smallFilter" placeholder="Small input" size="small"></app-filter-input>
 * <app-filter-input [(model)]="mediumFilter" placeholder="Medium input" size="medium"></app-filter-input>
 * <app-filter-input [(model)]="largeFilter" placeholder="Large input" size="large"></app-filter-input>
 *
 * <!-- Real-time filter example -->
 * <app-filter-input [(model)]="tableFilter" placeholder="Filter table..."></app-filter-input>
 * <!-- The filter value automatically clears when clear button (X) is clicked -->
 */

@Component({
    selector: 'app-filter-input',
    templateUrl: './filter-input.component.html',
    styleUrls: ['./filter-input.component.scss'],
    standalone: false
})
export class FilterInputComponent {
  @Input() placeholder: string = '';
  @Input() model: string = '';
  @Output() modelChange = new EventEmitter<string>();
  @Input() width: string = '350px';
  @Input() disabled: boolean = false;
  @Input() label: string = '';
  @Input() hasError: boolean = false;
  @Input() errorText: string = '';
  @Input() size: 'large' | 'medium' | 'small' = 'medium';
  @Output() enter = new EventEmitter<void>();

  get hasValue(): boolean {
    return !!this.model && this.model.length > 0;
  }

  onInput(value: string): void {
    this.model = value;
    this.modelChange.emit(this.model);
  }

  onClear(): void {
    if (!this.disabled) {
      this.model = '';
      this.modelChange.emit('');
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.enter.emit();
    }
  }
}

